/**
 * Shipping Countries Configuration
 *
 * List of countries where Higlo Hearths can be shipped.
 * Currently supports: United States, United Kingdom, and EU member states.
 */

export const ALLOWED_SHIPPING_COUNTRIES = [
  // North America
  { code: 'US', name: 'United States', postcodeLabel: 'ZIP Code', postcodePattern: /^\d{5}(-\d{4})?$/ },

  // United Kingdom
  { code: 'GB', name: 'United Kingdom', postcodeLabel: 'Postcode', postcodePattern: /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i },

  // EU Countries (27 member states, alphabetically)
  { code: 'AT', name: 'Austria', postcodeLabel: 'Postcode', postcodePattern: /^\d{4}$/ },
  { code: 'BE', name: 'Belgium', postcodeLabel: 'Postcode', postcodePattern: /^\d{4}$/ },
  { code: 'BG', name: 'Bulgaria', postcodeLabel: 'Postcode', postcodePattern: /^\d{4}$/ },
  { code: 'HR', name: 'Croatia', postcodeLabel: 'Postcode', postcodePattern: /^\d{5}$/ },
  { code: 'CY', name: 'Cyprus', postcodeLabel: 'Postcode', postcodePattern: /^\d{4}$/ },
  { code: 'CZ', name: 'Czech Republic', postcodeLabel: 'Postcode', postcodePattern: /^\d{3}\s?\d{2}$/ },
  { code: 'DK', name: 'Denmark', postcodeLabel: 'Postcode', postcodePattern: /^\d{4}$/ },
  { code: 'EE', name: 'Estonia', postcodeLabel: 'Postcode', postcodePattern: /^\d{5}$/ },
  { code: 'FI', name: 'Finland', postcodeLabel: 'Postcode', postcodePattern: /^\d{5}$/ },
  { code: 'FR', name: 'France', postcodeLabel: 'Postcode', postcodePattern: /^\d{5}$/ },
  { code: 'DE', name: 'Germany', postcodeLabel: 'Postcode', postcodePattern: /^\d{5}$/ },
  { code: 'GR', name: 'Greece', postcodeLabel: 'Postcode', postcodePattern: /^\d{3}\s?\d{2}$/ },
  { code: 'HU', name: 'Hungary', postcodeLabel: 'Postcode', postcodePattern: /^\d{4}$/ },
  { code: 'IE', name: 'Ireland', postcodeLabel: 'Eircode', postcodePattern: /^[A-Z]\d{2}\s?[A-Z0-9]{4}$/i },
  { code: 'IT', name: 'Italy', postcodeLabel: 'Postcode', postcodePattern: /^\d{5}$/ },
  { code: 'LV', name: 'Latvia', postcodeLabel: 'Postcode', postcodePattern: /^LV-\d{4}$/i },
  { code: 'LT', name: 'Lithuania', postcodeLabel: 'Postcode', postcodePattern: /^LT-\d{5}$/i },
  { code: 'LU', name: 'Luxembourg', postcodeLabel: 'Postcode', postcodePattern: /^\d{4}$/ },
  { code: 'MT', name: 'Malta', postcodeLabel: 'Postcode', postcodePattern: /^[A-Z]{3}\s?\d{4}$/i },
  { code: 'NL', name: 'Netherlands', postcodeLabel: 'Postcode', postcodePattern: /^\d{4}\s?[A-Z]{2}$/i },
  { code: 'PL', name: 'Poland', postcodeLabel: 'Postcode', postcodePattern: /^\d{2}-\d{3}$/ },
  { code: 'PT', name: 'Portugal', postcodeLabel: 'Postcode', postcodePattern: /^\d{4}-\d{3}$/ },
  { code: 'RO', name: 'Romania', postcodeLabel: 'Postcode', postcodePattern: /^\d{6}$/ },
  { code: 'SK', name: 'Slovakia', postcodeLabel: 'Postcode', postcodePattern: /^\d{3}\s?\d{2}$/ },
  { code: 'SI', name: 'Slovenia', postcodeLabel: 'Postcode', postcodePattern: /^\d{4}$/ },
  { code: 'ES', name: 'Spain', postcodeLabel: 'Postcode', postcodePattern: /^\d{5}$/ },
  { code: 'SE', name: 'Sweden', postcodeLabel: 'Postcode', postcodePattern: /^\d{3}\s?\d{2}$/ },
];

/**
 * Get country object by 2-letter code
 * @param {string} code - 2-letter country code (e.g., 'US', 'GB')
 * @returns {Object|undefined} Country object or undefined if not found
 */
export const getCountryByCode = (code) => {
  if (!code) return undefined;
  return ALLOWED_SHIPPING_COUNTRIES.find(c => c.code.toUpperCase() === code.toUpperCase());
};

/**
 * Get array of country codes for Google Maps country restrictions
 * @returns {string[]} Array of lowercase country codes
 */
export const getGoogleMapsCountryCodes = () => {
  return ALLOWED_SHIPPING_COUNTRIES.map(c => c.code.toLowerCase());
};

/**
 * Validate postcode/ZIP format for a specific country
 * @param {string} postcode - The postcode to validate
 * @param {string} countryCode - 2-letter country code
 * @returns {boolean} True if valid format
 */
export const validatePostcode = (postcode, countryCode) => {
  const country = getCountryByCode(countryCode);
  if (!country || !country.postcodePattern) return true; // If no pattern, skip validation
  return country.postcodePattern.test(postcode);
};

/**
 * Get postcode label for a specific country (e.g., "ZIP Code" vs "Postcode")
 * @param {string} countryCode - 2-letter country code
 * @returns {string} Label for postcode field
 */
export const getPostcodeLabel = (countryCode) => {
  const country = getCountryByCode(countryCode);
  return country ? country.postcodeLabel : 'Postcode';
};

/**
 * Check if a country code is in the allowed shipping list
 * @param {string} code - 2-letter country code
 * @returns {boolean} True if shipping is allowed to this country
 */
export const isShippingAllowed = (code) => {
  return getCountryByCode(code) !== undefined;
};
